import React from 'react';
import { ChevronDown, ListOrdered, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QueuedChatMessage } from '@/types';

interface QueuedMessagesPanelProps {
  queuedMessages: QueuedChatMessage[];
  onRemove: (id: string) => void;
  onCollapse: () => void;
}

const buildPreview = (message: QueuedChatMessage) => {
  const normalized = message.content.replace(/\s+/g, ' ').trim();
  return normalized || 'Queued attachment';
};

const QueuedMessagesPanel: React.FC<QueuedMessagesPanelProps> = ({ queuedMessages, onRemove, onCollapse }) => {
  if (queuedMessages.length === 0) {
    return null;
  }

  const countLabel = queuedMessages.length === 1 ? '1 Queued' : `${queuedMessages.length} Queued`;

  return (
    <div className="border-b border-border/70">
      <button
        type="button"
        onClick={onCollapse}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/20"
      >
        <ListOrdered className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="font-semibold text-foreground">{countLabel}</span>
        <span className="text-muted-foreground">- will send after current response</span>
        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      <div className="space-y-2 px-3 pb-3">
        {queuedMessages.map((queuedMessage, index) => {
          const attachmentCount = queuedMessage.attachments?.length || 0;

          return (
            <div
              key={queuedMessage.id}
              className="flex items-center gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm"
            >
              <span className="shrink-0 text-xs font-medium text-muted-foreground">#{index + 1}</span>
              <span className="min-w-0 flex-1 truncate text-foreground">{buildPreview(queuedMessage)}</span>
              {attachmentCount > 0 ? (
                <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                  <Paperclip className="h-3.5 w-3.5" />
                  <span>{attachmentCount}</span>
                </span>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                onClick={() => onRemove(queuedMessage.id)}
                aria-label={`Remove queued message ${index + 1}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QueuedMessagesPanel;
